import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { APP_COLORS } from "../../../enums";
import { appToast } from "../../notification/app-toast";
import FlexBox from "../../layouts/Flexbox";
import { FormHelperTextError } from "../errors";
import {
  aiBlockSx,
  aiBodySx,
  aiIconButtonSx,
  aiIconSx,
  aiLabelSx,
  aiPillSx,
  formControlSx,
  getBorderWrapperSx,
  getRefreshIconLoadingSx,
  getWriteWithAiButtonSx,
  innerStackSx,
} from "./textarea-ai-suggestion.styles";
import CheckIcon from '@mui/icons-material/Check';

export interface TextareaWithAiSuggestionProps {
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  fetchSuggestion?: (payload: Record<string, unknown>) => Promise<{ data?: { suggestion?: string } }>;
  getSuggestPayload?: () => Record<string, unknown>;
  suggestion?: string | null;
  onAcceptSuggestion?: () => void;
  onRejectSuggestion?: () => void;
  onRefreshClick?: () => void;
  suggestionLoading?: boolean;
  error?: string;
  helperText?: string;
  placeholder?: string;
  maxLength?: number;
  rows?: number;
  rowsWithSuggestion?: number;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  showAiButton?: boolean;
  minLengthForAi?: number;
}

const DEFAULT_PLACEHOLDER =
  "We have a project in plan for the fiscal year, with budget allocated, to evaluate solutions that will assess the security posture of SaaS applications.";

export function TextareaWithAiSuggestion({
  label,
  required = false,
  value,
  onChange,
  fetchSuggestion,
  getSuggestPayload,
  suggestion: suggestionProp,
  onAcceptSuggestion: onAcceptProp,
  onRejectSuggestion: onRejectProp,
  onRefreshClick: onRefreshProp,
  suggestionLoading: suggestionLoadingProp,
  error,
  helperText,
  placeholder = DEFAULT_PLACEHOLDER,
  rows = 4,
  rowsWithSuggestion = 2,
  inputRef,
  showAiButton = true,
  minLengthForAi,
}: TextareaWithAiSuggestionProps) {
  const [internalSuggestion, setInternalSuggestion] = useState<string | null>(null);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const useInternalApi = Boolean(fetchSuggestion);
  const suggestion = useInternalApi ? internalSuggestion : suggestionProp ?? null;
  const suggestionLoading = useInternalApi
    ? localLoading
    : (suggestionLoadingProp ?? false);

  const isSummaryEmpty = !(value?.trim()?.length > 0);
  const showRefreshLabel =
    !isSummaryEmpty && (hasFetchedOnce || suggestion !== null);

  const handleRefreshClick = async () => {
    if (useInternalApi && fetchSuggestion) {
      if (
        minLengthForAi != null &&
        (value ?? "").trim().length < minLengthForAi
      ) {
        appToast.error(
          `Please enter at least ${minLengthForAi} characters to use Write with AI.`
        );
        return;
      }
      setInternalSuggestion(null);
      setLocalLoading(true);
      try {
        const res = await fetchSuggestion(getSuggestPayload?.() ?? {}).catch(() => null);
        const s = res?.data?.description;
        if (typeof s === "string") {
          setInternalSuggestion(s);
        }
      } finally {
        setLocalLoading(false);
        setHasFetchedOnce(true);
      }
    } else {
      onRefreshProp?.();
    }
  };

  const handleAcceptSuggestion = () => {
    if (suggestion) {
      onChange(suggestion);
    }
    if (useInternalApi) {
      setInternalSuggestion(null);
    } else {
      onAcceptProp?.();
    }
  };

  const handleRejectSuggestion = () => {
    if (useInternalApi) {
      setInternalSuggestion(null);
    } else {
      onRejectProp?.();
    }
  };

  const hasSuggestion = suggestion !== null;

  return (
    <Stack gap={1}>
      <FlexBox alignItems="center" justifyContent={showAiButton ? "space-between" : "flex-start"}>
        <Typography variant="body-ibmPlexMono-bold" component="span">
          {label}
          {required && (
            <Typography component="span" color={APP_COLORS.error}>
              *
            </Typography>
          )}
        </Typography>

        <Button
          size="small"
          onClick={handleRefreshClick}
          disabled={suggestionLoading}
          startIcon={
            showRefreshLabel ? (
              <RefreshIcon sx={getRefreshIconLoadingSx(suggestionLoading)} />
            ) : (
              <AutoAwesomeIcon sx={{ color: APP_COLORS.white }} />
            )
          }
          sx={getWriteWithAiButtonSx(suggestionLoading)}
          style={{
            ...(!showAiButton && {
              visibility: "hidden",
            }),
          }}
        >
          {showRefreshLabel ? "Refresh" : "Write with AI"}
        </Button>

      </FlexBox>

      <FormControl variant="outlined" fullWidth error={!!error} sx={formControlSx}>
        <Box sx={getBorderWrapperSx(!!error, suggestionLoading)}>
          <Stack
            sx={{
              ...innerStackSx,
              ...(suggestionLoading && {
                position: "relative",
                zIndex: 1,
                m: "2px",
              }),
            }}
          >
            <TextField
              value={value}
              onChange={(e) => onChange(e.target.value)}
              inputRef={inputRef}
              size="small"
              fullWidth
              multiline
              rows={hasSuggestion ? rowsWithSuggestion : rows}
              placeholder={placeholder}
            />
            {hasSuggestion && (
              <Stack gap={1.25} sx={aiBlockSx}>
                <FlexBox sx={aiPillSx}>
                  <AutoAwesomeIcon sx={aiIconSx} />
                  <Typography sx={aiLabelSx}>AI Generate</Typography>
                </FlexBox>
                <Typography sx={aiBodySx}>{suggestion}</Typography>
                <FlexBox alignItems="flex-end" gap={1}>
                  <IconButton
                    onClick={handleAcceptSuggestion}
                    aria-label="Accept suggestion"
                    title="Accept"
                    sx={aiIconButtonSx}
                  >
                    <CheckIcon sx={aiIconSx} />
                  </IconButton>
                  <IconButton
                    onClick={handleRejectSuggestion}
                    aria-label="Reject suggestion"
                    title="Reject"
                    sx={aiIconButtonSx}
                  >
                    <CloseIcon sx={aiIconSx} />
                  </IconButton>
                </FlexBox>
              </Stack>
            )}
          </Stack>
        </Box>
        <FormHelperTextError error={error} helperText={helperText} />
      </FormControl>
    </Stack>
  );
}
